import { ReactElement, useMemo } from 'react'
import DeleteForever from '@mui/icons-material/DeleteForever'
import Preview from '@mui/icons-material/Preview'
import Straighten from '@mui/icons-material/Straighten'
import { Stack } from '@mui/material'
import useTapelineMutations from '@editor/hooks/useTapelineMutations'
import { ToggleButtonGroup, ToggleButton } from '@ui/actions'
import { Tapeline, useTapelineStore } from '@editorStores'
import TapelineSettingsPopup from './components/TapelineSettingsPopup'
import useTapelineModeHotkey from './useTapelineModeHotkey'

const TapelineControls = (): ReactElement => {
  useTapelineModeHotkey()
  const tapelineStore = useTapelineStore()
  const activeFilterViewId = useTapelineStore(state => state.activeFilterViewId)
  const { updateTapelineFields, deleteTapeline, deleteTapelinesInActiveFilterView } =
    useTapelineMutations()
  const hasLinesInActiveGroup = useMemo(
    () => tapelineStore.lines.some(line => line.filterViewId === activeFilterViewId),
    [tapelineStore.lines, activeFilterViewId],
  )

  const selectedLine = useMemo(
    () => tapelineStore.lines.find(line => line.id === tapelineStore.selectedId),
    [tapelineStore.lines, tapelineStore.selectedId],
  )

  const updateSelectedLine = (
    patch: Partial<
      Pick<Tapeline, 'showLengthLabel' | 'text' | 'style' | 'start' | 'end' | 'planConfig'>
    >,
  ): void => {
    if (!selectedLine) return
    updateTapelineFields(selectedLine.id, patch)
  }

  const closeTapelinePopover = (): void => {
    tapelineStore.deselectLine()
  }

  return (
    <Stack direction="row" spacing={1}>
      <ToggleButtonGroup size="small">
        <ToggleButton
          hint="Maßband zeichnen"
          value="active"
          size="small"
          onClick={() => {
            tapelineStore.setIsActive(!tapelineStore.isActive)
          }}
          selected={tapelineStore.isActive}
          Icon={Straighten}
          data-cy="btn-tapeline-draw"
        />

        <ToggleButton
          hint="Maßband anzeigen"
          value="visible"
          size="small"
          onClick={() => {
            tapelineStore.setIsVisible(!tapelineStore.isVisible)
          }}
          selected={tapelineStore.isVisible}
          Icon={Preview}
        />
        {hasLinesInActiveGroup && (
          <ToggleButton
            hint="Alle Maßbänder entfernen"
            value="delete"
            size="small"
            onClick={() => deleteTapelinesInActiveFilterView()}
            Icon={DeleteForever}
          />
        )}
      </ToggleButtonGroup>

      <TapelineSettingsPopup
        selectedId={tapelineStore.selectedId}
        selectedLine={selectedLine}
        onUpdateSelectedLine={updateSelectedLine}
        onDeleteSelectedLine={() => {
          if (!selectedLine) return
          deleteTapeline(selectedLine.id)
          closeTapelinePopover()
        }}
        onClose={closeTapelinePopover}
      />
    </Stack>
  )
}

export default TapelineControls
